import { createContext, useCallback, useContext, useEffect } from 'react'
import Box from '@mui/material/Box'
import { getPanelId, getTabId, useTabContext } from '@mui/lab/TabContext'

import { useRegisterFormFieldInFormTab } from './FormTabContext'

interface FormTabPanelProps {
  children?: React.ReactNode
  value: string
}

interface RegisterFormField {
  (field: string): void
}

const registerFormFieldCtx = createContext<RegisterFormField>((_field: string) => {})

export function useRegisterFormField(name: string) {
  const register = useContext(registerFormFieldCtx)

  useEffect(() => {
    register(name)
  }, [register, name])
}

export function FormTabPanel({ children, value }: FormTabPanelProps) {
  const context = useTabContext()
  const register = useRegisterFormFieldInFormTab()

  const registerFormField = useCallback(
    (field: string) => {
      register(value, field)
    },
    [value, register]
  )

  if (context === null) {
    throw new Error('FormTabPanel must be used within FormTabContext')
  }

  const panelId = getPanelId(context, value)
  const tabId = getTabId(context, value)

  return (
    <Box aria-labelledby={tabId} id={panelId} hidden={context.value !== value} p={2} role='tabpanel'>
      <registerFormFieldCtx.Provider value={registerFormField}>{children}</registerFormFieldCtx.Provider>
    </Box>
  )
}
